import { t } from "i18next";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable, DataTableFilterMetaData } from "primereact/datatable";
import { DataView } from "primereact/dataview";
import { Dialog } from "primereact/dialog";
import { Paginator } from "primereact/paginator";
import { TreeTable } from "primereact/treetable";
import React, { Fragment, useState } from "react";

export type TableHeader = {
  field: string;
  header: string;
  style?: any;
  sortable?: boolean;
  filterable?: boolean;
  beField?: string;
};

export type TableSettings = {
  sorting?: {
    field: TableHeader["field"];
    order: -1 | 0 | 1;
  };
  filters?: { [key: string]: DataTableFilterMetaData & { beField: string } };
  pagination?: {
    first: number;
    rows: number;
  };
  additional?: any;
};

export type TableProps = {
  items: any[];
  fields: (TableHeader & {
    body?:
      | React.ReactNode
      | ((data: any, options: ColumnBodyOptions) => React.ReactNode);
  })[];
  onRowClick?: (event: any) => void;
  rowActions?: {
    onItemMail?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
    onItemQR?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
    onItemEdit?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
    onItemDelete?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
    onItemShow?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
    onItemHide?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
    onItemRetry?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
    onItemComplete?: {
      command: (item: any) => void;
      condition?: (item: any) => boolean;
    };
  };
  loading?: boolean;
  treeTable?: boolean;
  pagination?: boolean;
  settings?: TableSettings;
  setSettings?: (settings: TableSettings) => void;
  totalRecords?: number;
  selectionMode?: boolean;
};

export default function Table(props: TableProps) {
  const isMobile = window.innerWidth <= 768;
  const { items, fields, settings, setSettings, onRowClick } = props;

  const [item, setItem] = useState(null);
  const [deleteItemDialog, setDeleteItemDialog] = useState(false);

  const confirmDeleteItem = (item: any) => {
    setItem(item);
    setDeleteItemDialog(true);
  };

  const hideDeleteItemDialog = () => {
    setDeleteItemDialog(false);
  };

  const deleteItem = () => {
    props.rowActions!.onItemDelete!.command(item);
    setDeleteItemDialog(false);
    setItem(null);
  };

  const actionBodyTemplate = (rowData: any) => {
    return (
      <React.Fragment>
        <div className="flex align-items-center justify-content-end">
          {props.rowActions?.onItemMail &&
            (props.rowActions.onItemMail?.condition
              ? props.rowActions.onItemMail.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-at"
                text
                onClick={() => props.rowActions!.onItemMail!.command(rowData)}
              />
            )}
          {props.rowActions?.onItemQR &&
            (props.rowActions.onItemQR?.condition
              ? props.rowActions.onItemQR.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-qrcode"
                text
                onClick={() => props.rowActions!.onItemQR!.command(rowData)}
              />
            )}
          {props.rowActions?.onItemEdit &&
            (props.rowActions.onItemEdit?.condition
              ? props.rowActions.onItemEdit.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-pencil"
                text
                onClick={() => props.rowActions!.onItemEdit!.command(rowData)}
              />
            )}
          {props.rowActions?.onItemShow &&
            (props.rowActions.onItemShow?.condition
              ? props.rowActions.onItemShow.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-eye"
                text
                onClick={() => props.rowActions!.onItemShow!.command(rowData)}
              />
            )}
          {props.rowActions?.onItemHide &&
            (props.rowActions.onItemHide?.condition
              ? props.rowActions.onItemHide.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-eye-slash"
                text
                onClick={() => props.rowActions!.onItemHide!.command(rowData)}
              />
            )}
          {props.rowActions?.onItemDelete &&
            (props.rowActions.onItemDelete?.condition
              ? props.rowActions.onItemDelete.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                onClick={() => confirmDeleteItem(rowData)}
              />
            )}
          {props.rowActions?.onItemRetry &&
            (props.rowActions.onItemRetry?.condition
              ? props.rowActions.onItemRetry.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-refresh"
                text
                onClick={() => props.rowActions!.onItemRetry!.command(rowData)}
              />
            )}
          {props.rowActions?.onItemComplete &&
            (props.rowActions.onItemComplete?.condition
              ? props.rowActions.onItemComplete.condition(rowData)
              : true) && (
              <Button
                icon="pi pi-check"
                text
                onClick={() =>
                  props.rowActions!.onItemComplete!.command(rowData)
                }
              />
            )}
        </div>
      </React.Fragment>
    );
  };

  const deleteItemDialogFooter = (
    <React.Fragment>
      <Button
        label={t("common.no")}
        icon="pi pi-times"
        outlined
        onClick={hideDeleteItemDialog}
      />
      <Button
        label={t("common.yes")}
        icon="pi pi-check"
        severity="danger"
        onClick={deleteItem}
      />
    </React.Fragment>
  );

  const itemTemplate = (item: any) => {
    return (
      <div className="p-2 w-full" onClick={() => onRowClick?.({ data: item })}>
        {fields.map((field) => (
          <div className="flex" key={field.field}>
            <strong className="flex-1">{field.header}</strong>
            <p>{item[field.field]}</p>
          </div>
        ))}
        {props.rowActions && actionBodyTemplate(item)}
      </div>
    );
  };

  return (
    <div style={{ wordBreak: "break-word" }}>
      {props.treeTable ? (
        isMobile ? (
          <Accordion>
            {items.map((item, index) => (
              <AccordionTab key={index} header={item.name}>
                <Table items={item.children} fields={fields}></Table>
              </AccordionTab>
            ))}
          </Accordion>
        ) : (
          <TreeTable value={items} loading={props.loading}>
            {fields.map(
              (
                field: TableHeader & {
                  body?:
                    | React.ReactNode
                    | ((
                        data: any,
                        options: ColumnBodyOptions,
                      ) => React.ReactNode);
                },
                index: number,
              ) => (
                <Column
                  style={field.style ? field.style : undefined}
                  expander={index === 0}
                  key={field.field}
                  field={field.field}
                  header={field.header}
                  sortable={field.sortable}
                  filter={field.filterable}
                  body={field.body ? field.body : undefined}
                ></Column>
              ),
            )}
          </TreeTable>
        )
      ) : (
        <Fragment>
          {isMobile ? (
            <DataView
              loading={props.loading}
              value={items}
              itemTemplate={itemTemplate}
            />
          ) : (
            <DataTable
              value={items}
              sortField={settings?.sorting?.field}
              sortOrder={settings?.sorting?.order}
              filters={settings?.filters}
              filterDisplay={!!settings?.filters ? "row" : undefined}
              tableStyle={{ minWidth: "50rem" }}
              onRowClick={onRowClick}
              loading={props.loading}
              selectionMode={props.selectionMode ? "single" : undefined}
              onSort={(e) => {
                if (!!setSettings) {
                  const beField =
                    fields.find((f) => f.field === e.sortField)?.beField ??
                    e.sortField;
                  setSettings({
                    ...settings,
                    sorting: {
                      field: beField!,
                      order: e.sortOrder!,
                    },
                  });
                }
              }}
              onFilter={(e) => {
                const eventFilters = JSON.parse(JSON.stringify(e.filters));
                if (!!eventFilters) {
                  for (const key in eventFilters) {
                    const beField =
                      fields.find((f) => f.field === key)?.beField ?? key;
                    eventFilters[key].beField = beField;
                  }
                }
                if (!!setSettings) {
                  setSettings({
                    ...settings,
                    filters: eventFilters,
                    pagination: {
                      first: 0,
                      rows: settings?.pagination?.rows ?? 10,
                    },
                  });
                }
              }}
            >
              {fields.map(
                (
                  field: TableHeader & {
                    body?:
                      | React.ReactNode
                      | ((
                          data: any,
                          options: ColumnBodyOptions,
                        ) => React.ReactNode);
                  },
                ) => (
                  <Column
                    key={field.field}
                    field={field.field}
                    header={field.header}
                    sortable={field.sortable}
                    filter={field.filterable}
                    body={field.body ? field.body : undefined}
                  ></Column>
                ),
              )}
              {props.rowActions && (
                <Column
                  body={actionBodyTemplate}
                  exportable={false}
                  align="right"
                  style={{ width: "112px" }}
                ></Column>
              )}
            </DataTable>
          )}
          {props.pagination && (
            <Paginator
              first={settings?.pagination?.first}
              rows={settings?.pagination?.rows}
              totalRecords={props.totalRecords ?? items.length}
              rowsPerPageOptions={[5, 10, 20, 30]}
              onPageChange={(e) => {
                if (!!setSettings) {
                  setSettings({
                    ...settings,
                    pagination: {
                      first: e.first,
                      rows: e.rows,
                    },
                  });
                }
              }}
              style={{
                borderBottom: "1px solid #e2e8f0",
                borderRadius: "0",
              }}
            />
          )}
        </Fragment>
      )}
      <Dialog
        visible={deleteItemDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header={t("common.confirm")}
        modal
        footer={deleteItemDialogFooter}
        onHide={hideDeleteItemDialog}
      >
        <div className="confirmation-content flex align-items-center justify-content-center gap-3">
          {item && (
            <Fragment>
              <i
                className="pi pi-exclamation-triangle mr-3"
                style={{ fontSize: "2rem" }}
              />
              <span>
                {t("common.areYouSureYouWantToDelete", {
                  name:
                    (item as any).name ??
                    (item as any).title ??
                    (item as any).id,
                })}
              </span>
            </Fragment>
          )}
        </div>
      </Dialog>
    </div>
  );
}
